import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeState {
  /** Was der Nutzer gewählt hat. */
  preference: ThemePreference;
  /** Was daraus tatsächlich angezeigt wird. */
  theme: ResolvedTheme;
  setPreference: (p: ThemePreference) => void;
}

const STORAGE_KEY = 'nexply-theme';
const ThemeContext = createContext<ThemeState | null>(null);

function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function readPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPref] = useState<ThemePreference>(readPreference);
  const [system, setSystem] = useState<ResolvedTheme>(systemTheme);

  // Der Systemwechsel muss auch dann greifen, wenn die App gerade offen ist.
  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = (e: MediaQueryListEvent) => setSystem(e.matches ? 'light' : 'dark');
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const theme: ResolvedTheme = preference === 'system' ? system : preference;

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    // Damit native Elemente (Scrollbars, Formularfelder, Datepicker) mitziehen.
    root.style.colorScheme = theme;

    // Browser-/Statusleiste nachziehen. Die media-basierten theme-color-Tags in index.html
    // folgen dem System — bei manueller Wahl wäre die Leiste sonst im falschen Ton.
    let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]:not([media])');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = theme === 'light' ? '#FAF8F4' : '#0A0908';
  }, [theme]);

  const setPreference = useCallback((p: ThemePreference) => {
    setPref(p);
    window.localStorage.setItem(STORAGE_KEY, p);
  }, []);

  const value = useMemo(
    () => ({ preference, theme, setPreference }),
    [preference, theme, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const v = useContext(ThemeContext);
  if (!v) throw new Error('useTheme must be inside ThemeProvider');
  return v;
}
